import { constructUrlWithQueryParams, investmentEntityApsRequest, investmentEntityRequest } from '@utils/request';
import {
	CurrentPrincipal,
	IpoInvestmentPosition,
	IpoPositionDetail,
	Positions,
	ReitPositionDetail
} from 'types/portfolio';
import { RiaPlan, SignupFundPlanListResponse, SignupRiaPlanListResponse } from 'types/plan';
import { AxiosResponse } from 'axios';
import { CustomSpacetimeFormat } from 'types/spacetime-formats';
import { formatPerformanceResponseData } from '@utils/account-performance-table-formatter';
import { ModularNewsfeedResponse } from 'types/account';
import { NewsfeedQueryParams } from 'types/sanity/proxy';
import { Performance } from 'types/performance';
import spacetime from 'spacetime';

export async function getPerformanceData(): Promise<Performance> {
	const response = await investmentEntityApsRequest.get(`/performance`);
	const data: Performance = response.data;
	return formatPerformanceResponseData(data);
}

export async function getPositionsTable(): Promise<Positions> {
	const response = await investmentEntityApsRequest.get(
		`/portfolio/positions-table?separateGrowthEquityPositions=true`
	);
	return response.data;
}

export async function getNewsFeedByPageNumber(params: NewsfeedQueryParams): Promise<ModularNewsfeedResponse> {
	const urlWithParams = constructUrlWithQueryParams('/newsfeed', params);
	const response = await investmentEntityRequest.get(urlWithParams);
	const availableFilters = response.data.availableFilters ?? [];
	const totalNumber = response.data.data.totalItems ?? '0';

	return {
		items: response.data.data.results,
		availableFilters,
		totalNumber
	};
}

export async function getReitPositionDetail(reitId: string): Promise<ReitPositionDetail> {
	const response = await investmentEntityApsRequest.get(`/reit/${reitId}/position`);
	return response.data;
}

export async function getIpoPositionDetail(): Promise<IpoPositionDetail> {
	const response = await investmentEntityRequest.get(`/ipo/position`);
	response.data.ipoInvestmentPositions.forEach((ipoInvestmentPosition: IpoInvestmentPosition) => {
		ipoInvestmentPosition.investmentDate = spacetime(ipoInvestmentPosition.investmentDate).format(
			CustomSpacetimeFormat.MONTH_SHORT_DATE_YEAR
		);
	});
	return response.data;
}

export async function getCurrentRiaPlan(): Promise<RiaPlan> {
	const response = await investmentEntityRequest.get(`/ria-plan`, {
		suppressToast: (resp: AxiosResponse) => resp.status === 404
	});
	return response.data;
}

export async function getAvailableSignupPlansV2(filter?: 'recommended'): Promise<SignupRiaPlanListResponse> {
	const endpoint = '/signup-ria-plans-v2';
	let response;

	if (filter) {
		response = await investmentEntityRequest.get(endpoint, {
			params: {
				filter
			}
		});
	} else {
		response = await investmentEntityRequest.get(endpoint);
	}

	return response.data;
}

export async function fetchAvailableSignupFundPlans(): Promise<Array<SignupFundPlanListResponse>> {
	const response = await investmentEntityRequest.get('/signup-funds');
	return response.data;
}

export async function updateRiaPlan(planId: string): Promise<void> {
	await investmentEntityRequest.post(`/ria-plan/${planId}/update`);
}

export async function getCurrentPrincipal(): Promise<CurrentPrincipal> {
	const response = await investmentEntityApsRequest.get(`/principal`);
	return response.data;
}

export async function clearPlanAndProSelections(): Promise<void> {
	await investmentEntityRequest.post(`/signup-checkout/clear-selections`);
}

export async function syncSignupFlow(): Promise<void> {
	await investmentEntityRequest.put('/sync-signup-flow');
}
